import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { SafeJsonLd } from "@/lib/safeHtml";

/**
 * FAQ block with JSON-LD schema for Google rich results.
 *
 * Props:
 *   eyebrow  – small uppercase label (e.g. "Preguntas frecuentes")
 *   title    – section title (h2)
 *   items    – [{q: string, a: string}]
 */
const FAQ = ({ eyebrow = "Preguntas frecuentes", title, items = [] }) => {
  const [open, setOpen] = useState(0);

  // JSON-LD schema for Google rich results
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };

  return (
    <section className="py-20 md:py-28 bg-ivory" data-testid="faq-section">
      <SafeJsonLd data={jsonLd} />
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{eyebrow}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {title}
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-8">
          <ul className="divide-y divide-sand">
            {items.map((it, i) => {
              const isOpen = open === i;
              return (
                <li key={it.q} data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full text-left py-6 flex items-start gap-5 group"
                    aria-expanded={isOpen}
                    data-testid={`faq-question-${i}`}
                  >
                    <span className="font-serif text-2xl text-charcoal flex-1 leading-snug group-hover:text-terracotta transition-colors">
                      {it.q}
                    </span>
                    <span
                      className={`mt-2 w-9 h-9 rounded-full border border-charcoal/20 flex items-center justify-center text-charcoal/70 shrink-0 transition-transform ${isOpen ? "rotate-180 bg-terracotta/15 border-terracotta/40 text-terracotta" : ""}`}
                    >
                      <ChevronDown size={16} />
                    </span>
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ${isOpen ? "grid-rows-[1fr] pb-6" : "grid-rows-[0fr]"}`}
                  >
                    <div className="overflow-hidden">
                      <p
                        className="text-charcoal/75 leading-relaxed text-[16px] max-w-2xl"
                        data-testid={`faq-answer-${i}`}
                      >
                        {it.a}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};

export default FAQ;
